<template>
  <div id="OverviewDetails">
    <el-tabs
      v-if="$mq === 'laptop' || $mq === 'desktop'"
      v-loading="loading"
      type="border-card"
      class="mainCard clear-tabs"
      :stretch="true"
      lazy
    >
      <div class="filter mainCard">
        <el-select
          v-model="filter"
          :placeholder="$t('common.select')"
        >
          <el-option
            v-for="item in filterOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </div>

      <el-tab-pane>
        <div slot="label">
          <div class="title">
            {{ $t('common.lgaLongSingle') }} 1
          </div>
          <div class="value">
            <b>{{ lgas.lga1 }}</b>
          </div>
        </div>

        <OverviewDetailsPane
          :action-plan-status="actionPlanStatus"
          :action-plan="filteredActionPlan.lga1"
          :color="'#09eacf'"
        />
      </el-tab-pane>

      <el-tab-pane v-if="lgas.lga2">
        <div slot="label">
          <div class="title">
            {{ $t('common.lgaLongSingle') }} 2
          </div>
          <div class="value">
            <b>{{ lgas.lga2 }}</b>
          </div>
        </div>

        <OverviewDetailsPane
          :action-plan-status="actionPlanStatus"
          :action-plan="filteredActionPlan.lga2"
          :color="'#dfe500'"
        />
      </el-tab-pane>
    </el-tabs>

    <div
      v-else
      v-loading="loading"
    >
      <div
        v-for="(lga, lKey) in ['lga1', 'lga2']"
        :key="lKey"
      >
        <div class="mValue">
          <b>{{ lgas[lga] }}</b>
        </div>

        <OverviewDetailsPane
          :action-plan-status="actionPlanStatus"
          :action-plan="filteredActionPlan[lga]"
          :filter-option="filterOptions"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import api from '@/api';
import OverviewDetailsPane from './OverviewDetailsPane.vue';

export default {
  components: {
    OverviewDetailsPane,
  },

  props: {
    stats: {
      type: Array,
      default: null,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    engagement: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      filter: '',
      leader: null, // Only used if user watching page is a coach
    };
  },

  computed: {
    ...mapState({
      role_type: (state) => state.user.user.role_type,
      vuexLgas: (state) => state.myLGAs.lgas,
      vuexSession: (state) => state.processStatus.process.session,
    }),

    lgas() {
      // if current user is leader:
      //   if checking current engagement: we get lga from vuex state
      //   if checking past engagement: we get lga from props
      // if it s a coach watching leader action plan, we get lgas from api
      if (this.role_type === 'leader') {
        if (this.engagement) {
          return {
            lga1: this.engagement.lga1,
            lga2: this.engagement.lga2,
            status: 'published',
          };
        }
        return this.vuexLgas;
      }
      if (this.leader) return this.leader.lga;
      return {};
    },

    session() {
      if (this.role_type === 'leader') {
        if (this.engagement) {
          return this.engagement.duration;
        }
        return this.vuexSession;
      }

      if (this.leader && this.leader.session_id) {
        if (this.leader.session_id === 'on_boarding') return 0;
        const session = this.leader.session_id.split('_')[1];
        return parseInt(session, 10);
      }
      return 0;
    },

    actionPlanStatus() {
      let status = 'noData';
      const filterSession = this.filter.split('_')[1] * 1;

      if (!this.stats) return status;

      const matching = this.stats.find((el) => el.session_id === this.filter);

      if ((this.session === filterSession) && !matching) {
        status = 'notPublished';
      } else if ((this.session > filterSession) && !matching) {
        status = 'notCreated';
      } else {
        status = 'exist';
      }

      return status;
    },

    filteredActionPlan() {
      let lgas = {
        lga1: {
          daily: [],
          weekly: [],
          monthly: [],
        },
        lga2: {
          daily: [],
          weekly: [],
          monthly: [],
        },
      };

      if (!this.stats) return lgas;

      const matching = this.stats.find((el) => el.session_id === this.filter);
      if (matching) lgas = matching;

      return lgas;
    },

    filterOptions() {
      const options = [];

      if (['desktop', 'laptop'].includes(this.$mq)) {
        let cpt = this.session;
        let data;

        if (cpt >= 4) {
          do {
            data = {
              value: `session_${cpt}`,
              label: `${this.$t('actionPlan.session')} ${cpt}`,
              disabled: true,
            };
            if (this.stats) {
            // eslint-disable-next-line no-loop-func
              data.disabled = !this.stats.some((el) => el.session_id === `session_${cpt}`);
            }
            options.push(data);
            cpt -= 1;
          } while (cpt >= 4);
        }
      }

      return options;
    },
  },

  watch: {
    session() {
      if (this.role_type === 'leader') {
        this.filter = `session_${this.session}`;
      }
    },
  },

  created() {
    if (this.role_type !== 'leader') {
      this.getLeaderDetails();
    }
  },

  mounted() {
    if (this.role_type === 'leader') {
      this.filter = `session_${this.session}`;
    }
  },

  methods: {
    async getLeaderDetails() {
      try {
        let response = {};
        if (this.engagement?.leader_info?.uuid) {
          response = await api.getCoachLeaderDetails(this.engagement.leader_info.uuid);
        } else {
          response = await api.getCoachLeaderDetails(this.$route.params.id);
        }
        this.leader = response.data;
        this.filter = this.leader.session_id;
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
        if (err.response && err.response.status === 404) {
          this.$router.push({ name: '404' });
        }
      }
    },
  },

};
</script>

<style lang="scss" scoped>
  #OverviewDetails {
    .el-tabs__header {
      .value {
        font-size: 16px;
      }
    }

    .filter {
      text-align: right;
    }

    .mValue {
      color: var(--themeFontColor);
      padding: 0 10px;
      word-break: break-word;
    }
  }
</style>

<style lang="scss">
  #OverviewDetails {
    .el-tabs__nav-scroll {
      display: flex;

      .el-tabs__nav {
        align-items: stretch;

        .el-tabs__item {
          height: 100%;

          & > div {
            height: 100%;
          }

          .value {
            display: flex;
            height: calc(100% - 30px);
            justify-content: center;
            align-items: center;
            padding: 5px 0;
            box-sizing: border-box;
            white-space: normal;
            word-break: break-word;
            line-height: 18px;
          }
        }
      }
    }

    .el-tabs__content {
      padding: 20px 30px;
    }

    .el-select {
      .el-input {
        input {
          border-radius: 20px;
          background-color: transparent;
          color: var(--themeFontColor);
          border-color: var(--themeFontColor);
        }
      }
    }
  }
</style>
